import * as React from "react"

export type BookingUrlStep = "dates" | "addons" | "details" | "payment"
const steps: BookingUrlStep[] = ["dates", "addons", "details", "payment"]

function requestedStep(): BookingUrlStep | null {
    const value = new URL(window.location.href).searchParams.get("step")
    return steps.includes(value as BookingUrlStep) ? value as BookingUrlStep : null
}

function writeStep(step: BookingUrlStep, mode: "push" | "replace") {
    const url = new URL(window.location.href)
    url.searchParams.set("step", step)
    // Keep product/attribution parameters, hashes and other history users' state.
    // Customer information and checkout credentials stay out of the URL/history.
    if (mode === "push" && url.href !== window.location.href) window.history.pushState(window.history.state, "", url)
    else window.history.replaceState(window.history.state, "", url)
}

export function useBookingHistory(options: {
    ready: boolean
    locked: boolean
    step: BookingUrlStep
    resolve: (step: BookingUrlStep) => BookingUrlStep
    apply: (step: BookingUrlStep) => void
    onNavigate: () => void
}) {
    const latest = React.useRef(options)
    latest.current = options
    const initialized = React.useRef(false)

    React.useEffect(() => {
        if (!options.ready || initialized.current) return
        initialized.current = true
        const step = latest.current.resolve(requestedStep() ?? latest.current.step)
        writeStep(step, "replace")
        latest.current.apply(step)
    }, [options.ready])

    React.useEffect(() => {
        const pop = () => {
            if (!initialized.current) return // Restoration reads the latest URL when ready.
            latest.current.onNavigate()
            const step = latest.current.resolve(requestedStep() ?? "dates")
            writeStep(step, "replace")
            latest.current.apply(step)
        }
        window.addEventListener("popstate", pop)
        return () => window.removeEventListener("popstate", pop)
    }, [])

    return React.useCallback((step: BookingUrlStep, mode: "push" | "replace" = "push") => {
        if (latest.current.locked) return
        if (step !== latest.current.step) latest.current.onNavigate()
        if (initialized.current) writeStep(step, mode)
        latest.current.apply(step)
    }, [])
}
